/**
 * @fileoverview Errors thrown by a flagged feature, attributed to its flag.
 * @module lib/features/core/flagErrors
 *
 * A flag's error rate counts only errors from the code the flag guards — never
 * page-wide browser errors, which say nothing about the feature. They reach the
 * dashboard as an `error_event` carrying `flagKey` and `flagVariant`:
 *
 *   - `<Flag>` catches errors thrown by its children (React).
 *   - `flags.run(key, newPath, oldPath)` catches errors in either path.
 *   - `flags.reportError(key, error)` for anything else.
 *   - On a server: `fg.trackError(error, { flag: 'key' })`.
 *
 * When the new path throws, the visitor gets the old path instead of a broken
 * page. The error is still logged to the console so the app's own tooling sees it.
 */

const MAX_MESSAGE = 512;
const MAX_STACK = 2048;

export interface FlagErrorPayload {
  type: 'error_event';
  message: string;
  stack?: string;
  errorType: string;
  fatal: false;
  context: string;
  flagKey: string;
  flagVariant: string;
}

export type ReportFlagError = (key: string, error: unknown) => void;

export function toFlagErrorPayload(key: string, variant: string, error: unknown): FlagErrorPayload {
  const err = error instanceof Error ? error : null;
  const message = err ? err.message : String(error);

  return {
    type: 'error_event',
    message: (message || 'Unknown error').slice(0, MAX_MESSAGE),
    ...(err?.stack ? { stack: err.stack.slice(0, MAX_STACK) } : {}),
    errorType: err?.name || 'Error',
    fatal: false,
    context: `flag:${key}`,
    flagKey: key,
    flagVariant: variant,
  };
}

function isPromise<T>(value: unknown): value is Promise<T> {
  return !!value && typeof (value as Promise<T>).then === 'function';
}

/**
 * Run the path this visitor's arm gets.
 *
 * An error in the new path is reported and the old path runs instead. An error
 * in the old path is reported and rethrown — there is nothing left to fall back
 * to, and swallowing it would hide a real outage. Works for sync and async paths.
 */
export function runFlagged<T>(
  key: string,
  enabled: boolean,
  newPath: () => T,
  oldPath: () => T,
  report: ReportFlagError,
): T {
  const fallBack = (error: unknown): T => {
    report(key, error);
    logToConsole(key, error);
    return oldPath();
  };

  const surface = (error: unknown): never => {
    report(key, error);
    throw error;
  };

  if (!enabled) {
    try {
      const result = oldPath();
      return (isPromise(result) ? result.catch(surface) : result) as T;
    } catch (error) {
      return surface(error);
    }
  }

  try {
    const result = newPath();
    return (isPromise(result) ? result.catch(fallBack) : result) as T;
  } catch (error) {
    return fallBack(error);
  }
}

/** The app's own error tooling should still see a caught feature error. */
export function logToConsole(key: string, error: unknown): void {
  try {
    console.error(`[Flowgrid] "${key}" threw; the fallback was shown instead.`, error);
  } catch {
    /* console unavailable */
  }
}
